import {
  Link,
  Outlet,
  createFileRoute,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Building2,
  MapPin,
  PackageCheck,
  Plus,
  Receipt,
  Truck,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import type {
  PaginatedResponse,
  PurchaseOrder,
  Vendor,
  VendorDetail,
} from '@/lib/api/types';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
  Toolbar,
} from '@/ui/Panel';
import { DataTable, type Column } from '@/ui/DataTable';
import { SplitView } from '@/ui/SplitView';
import { Pagination } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { SearchInput } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { useShortcutHint } from '@/app/StatusLine';
import { countLabel, money, num, phone, smartDate } from '@/lib/format';
import { poKeys, vendorKeys } from '@/features/buying/api';
import { PoStatusBadge } from '@/features/buying/parts';
import type { PoSummary } from '@/features/buying/types';
import { asText } from '@/lib/searchParams';

const PAGE_SIZE = 50;

const SORTS = new Set(['name', 'contact', 'terms']);

/** Optional throughout: `/vendors/*` children inherit this schema. */
export type VendorSearch = {
  id?: number;
  q?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
};

export const Route = createFileRoute('/_app/vendors')({
  staticData: { crumb: 'Vendors' },
  validateSearch: (s: Record<string, unknown>): VendorSearch => ({
    id: Number(s.id) > 0 ? Number(s.id) : undefined,
    q: asText(s.q, ''),
    sort: typeof s.sort === 'string' && SORTS.has(s.sort) ? s.sort : 'name',
    order: s.order === 'desc' ? 'desc' : 'asc',
    page: Number(s.page) > 0 ? Number(s.page) : 1,
  }),
  component: VendorsRoute,
});

function VendorsRoute() {
  const children = useChildMatches();
  if (children.length > 0) return <Outlet />;
  return <VendorList />;
}

function VendorList() {
  const raw = Route.useSearch();
  const search = {
    id: raw.id,
    q: raw.q ?? '',
    sort: raw.sort ?? 'name',
    order: raw.order ?? ('asc' as const),
    page: raw.page ?? 1,
  };
  const navigate = useNavigate({ from: Route.fullPath });

  useShortcutHint([
    { keys: 'j / k', label: 'move' },
    { keys: '↵', label: 'inspect' },
  ]);

  const list = useQuery({
    queryKey: vendorKeys.list({
      q: search.q,
      sort: search.sort,
      order: search.order,
      page: search.page,
    }),
    queryFn: () =>
      api.get<PaginatedResponse<Vendor>>('/vendors', {
        params: {
          ...(search.q ? { search: search.q } : {}),
          sort: search.sort,
          order: search.order,
          page: search.page,
          pageSize: PAGE_SIZE,
        },
      }),
  });

  const rows = list.data?.data ?? [];
  const setSearch = (next: VendorSearch) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }) });

  const columns: Column<Vendor>[] = [
    {
      id: 'name',
      header: 'Vendor',
      sortable: true,
      cell: (v) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="text-ink truncate">{v.name}</span>
          {v.active === false ? <Badge tone="neutral">inactive</Badge> : null}
        </span>
      ),
    },
    {
      id: 'contact',
      header: 'Contact',
      width: '12rem',
      sortable: true,
      hideBelow: 'md',
      cell: (v) => v.contactName ?? <span className="text-ink-faint">—</span>,
    },
    {
      id: 'phone',
      header: 'Phone',
      width: '9rem',
      mono: true,
      hideBelow: 'lg',
      cell: (v) => (v.phone ? phone(v.phone) : <span className="text-ink-faint">—</span>),
    },
    {
      id: 'terms',
      header: 'Terms',
      width: '8rem',
      sortable: true,
      hideBelow: 'lg',
      cell: (v) => v.terms ?? <span className="text-ink-faint">—</span>,
    },
    {
      id: 'parsing',
      header: 'Parsing notes',
      width: '9rem',
      hideBelow: 'xl',
      cell: (v) =>
        v.invoiceParsingNotesAuto ? (
          <Badge tone="warn">review pending</Badge>
        ) : v.invoiceParsingNotes ? (
          <Badge tone="info">in use</Badge>
        ) : (
          <span className="text-2xs text-ink-faint">—</span>
        ),
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Stock"
        title="Vendors"
        subtitle="Suppliers, their catalogs and their invoice-parsing rules"
        actions={
          <>
            <Button
              size="sm"
              variant="default"
              asChild
              icon={<Receipt className="size-3.5" />}
            >
              <Link to="/vendors/cost-approvals" search={{}}>
                Cost approvals
              </Link>
            </Button>
            {/* TASK-0256 (DROP-1): where vendors drop our goods; no rail item (two Buying items). */}
            <Button
              size="sm"
              variant="default"
              asChild
              icon={<MapPin className="size-3.5" />}
            >
              <Link to="/vendors/drop-off-locations">Drop-off locations</Link>
            </Button>
            <Button
              size="sm"
              variant="primary"
              asChild
              icon={<Plus className="size-3.5" />}
            >
              <Link to="/vendors/new">New vendor</Link>
            </Button>
          </>
        }
      />

      <Toolbar>
        <SearchInput
          size="sm"
          className="w-72"
          value={search.q}
          onValueChange={(q) => setSearch({ q, page: 1 })}
          placeholder="Name, contact or phone…"
        />
        <span className="text-2xs text-ink-faint ml-auto font-mono tabular-nums">
          {num(list.data?.total ?? 0)} vendors
        </span>
      </Toolbar>

      <PageBody bleed className="flex min-h-0">
        <SplitView
          storageKey="vendors"
          defaultDetail={460}
          onCloseDetail={() => setSearch({ id: undefined })}
          placeholder={
            <EmptyState
              compact
              icon={<Building2 />}
              title="No vendor selected"
              description="Pick a supplier to see its catalog, purchase orders and parsing notes."
            />
          }
          detail={search.id ? <VendorInspector id={search.id} /> : undefined}
          list={
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(v) => v.id}
              loading={list.isPending}
              error={list.error}
              onRetry={() => void list.refetch()}
              isRowActive={(v) => v.id === search.id}
              rowTone={(v) => (v.active === false ? 'negative' : null)}
              onRowOpen={(v) => setSearch({ id: v.id })}
              sort={{
                by: search.sort,
                dir: search.order,
                onChange: (by, dir) => setSearch({ sort: by, order: dir, page: 1 }),
              }}
              empty={{
                icon: <Building2 />,
                title: 'No vendors',
                description: search.q
                  ? 'No supplier matches that search.'
                  : 'Add the suppliers you buy from to unlock cost prefill and invoice import.',
                action: (
                  <Button size="sm" asChild>
                    <Link to="/vendors/new">New vendor</Link>
                  </Button>
                ),
              }}
              footer={
                <Pagination
                  className="w-full"
                  page={search.page}
                  pageSize={PAGE_SIZE}
                  total={list.data?.total ?? 0}
                  onPageChange={(page) => setSearch({ page })}
                />
              }
            />
          }
        />
      </PageBody>
    </Page>
  );
}

/* ── Inspector ─────────────────────────────────────────────────────────────── */

function VendorInspector({ id }: { id: number }) {
  const vendor = useQuery({
    queryKey: vendorKeys.detail(id),
    queryFn: () => api.get<VendorDetail>(`/vendors/${id}`),
  });

  /** Filter-wide, not page-limited — the tiles below must not undercount. */
  const summary = useQuery({
    queryKey: poKeys.summary({ vendorId: id }),
    queryFn: () =>
      api.get<PoSummary>('/vendors/purchase-orders/summary', {
        params: { vendorId: id },
      }),
  });

  const recent = useQuery({
    queryKey: poKeys.list({ vendorId: id, page: 1, recent: true }),
    queryFn: () =>
      api.get<PaginatedResponse<PurchaseOrder>>('/vendors/purchase-orders', {
        params: { vendorId: id, page: 1, pageSize: 8 },
      }),
  });

  if (vendor.isPending) {
    return (
      <div className="space-y-3 p-3">
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (vendor.isError || !vendor.data) {
    return (
      <div className="p-3">
        <ErrorState
          title="Could not load this vendor"
          message={vendor.error instanceof Error ? vendor.error.message : undefined}
          onRetry={() => void vendor.refetch()}
        />
      </div>
    );
  }

  const v = vendor.data;
  const counts = summary.data?.statusCounts ?? {};
  const openPos =
    (counts.draft ?? 0) + (counts.submitted ?? 0) + (counts.partially_received ?? 0);

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-ink truncate text-base font-semibold">{v.name}</div>
          <div className="text-ink-muted truncate text-xs">
            {v.contactName ?? 'No named contact'}
          </div>
        </div>
        {v.active === false ? <Badge tone="negative">Inactive</Badge> : null}
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          asChild
          icon={<ArrowRight className="size-3.5" />}
          className="flex-1"
        >
          <Link to="/vendors/$id" params={{ id: String(v.id) }}>
            Open vendor
          </Link>
        </Button>
        <Button size="sm" variant="default" asChild>
          <Link to="/purchasing/new" search={{ vendorId: v.id }}>
            New PO
          </Link>
        </Button>
      </div>

      <StatRow className="grid-cols-3 md:grid-cols-3 xl:grid-cols-3">
        <Stat
          label="Open POs"
          icon={<PackageCheck />}
          value={summary.isPending ? '—' : num(openPos)}
          tone={openPos > 0 ? 'warn' : undefined}
        />
        <Stat
          label="PO value"
          icon={<Truck />}
          value={summary.isPending ? '—' : money(summary.data?.totalValue ?? 0)}
          sub={`${num(summary.data?.totalCount ?? 0)} orders`}
        />
        <Stat label="Linked items" value={num(v.items?.length ?? 0)} />
      </StatRow>

      <Panel title="Contact">
        <DetailList columns={2}>
          <Detail label="Phone" mono>
            {v.phone ? phone(v.phone) : '—'}
            {v.phoneExt ? ` ×${v.phoneExt}` : ''}
          </Detail>
          <Detail label="Fax" mono>
            {v.fax ? phone(v.fax) : '—'}
          </Detail>
          <Detail label="Email" className="col-span-2">
            {v.email ?? '—'}
          </Detail>
          <Detail label="Terms">{v.terms ?? '—'}</Detail>
          <Detail label="Code" mono>
            {v.legacyCode ? <Code>{v.legacyCode}</Code> : '—'}
          </Detail>
          {v.addressLine1 ? (
            <Detail label="Address" className="col-span-2">
              {[v.addressLine1, v.addressLine2, v.city, v.state, v.zip]
                .filter(Boolean)
                .join(', ')}
            </Detail>
          ) : null}
        </DetailList>
      </Panel>

      {v.invoiceParsingNotesAuto ? (
        <Panel title="Parser notes awaiting review">
          <p className="text-ink text-xs leading-relaxed whitespace-pre-wrap">
            {v.invoiceParsingNotesAuto}
          </p>
          <p className="text-2xs text-ink-faint mt-2">
            Written by the parser, never fed back into a parse until promoted. Promote or
            discard it on the vendor page.
          </p>
        </Panel>
      ) : null}

      <Panel
        title={`Recent purchase orders`}
        bleed
        bodyClassName="divide-y divide-line-soft"
      >
        {recent.isPending ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ) : (recent.data?.data.length ?? 0) === 0 ? (
          <div className="text-ink-faint p-3 text-xs">No purchase orders yet.</div>
        ) : (
          (recent.data?.data ?? []).map((po) => (
            <Link
              key={po.id}
              to="/purchasing/$id"
              params={{ id: String(po.id) }}
              search={{ mode: 'lines' as const }}
              className="hover:bg-surface-hover flex items-center gap-2 px-3 py-1.5"
            >
              <span className="text-2xs text-ink w-14 shrink-0 font-mono tabular-nums">
                #{po.id}
              </span>
              <PoStatusBadge status={po.status} closedShort={po.closedShortAt != null} />
              <span className="text-2xs text-ink-muted ml-auto shrink-0 font-mono tabular-nums">
                {money(po.total ?? 0)}
              </span>
              <span className="text-2xs text-ink-faint w-16 shrink-0 text-right font-mono tabular-nums">
                {smartDate(po.updatedAt)}
              </span>
            </Link>
          ))
        )}
      </Panel>

      <Panel title={`Catalog · ${countLabel(v.items?.length ?? 0, 'product')}`} bleed>
        {(v.items?.length ?? 0) === 0 ? (
          <div className="text-ink-faint p-3 text-xs">
            No products linked. Linking them gives cost prefill on POs and better invoice
            matching.
          </div>
        ) : (
          <div className="divide-line-soft max-h-64 divide-y overflow-y-auto">
            {(v.items ?? []).slice(0, 40).map((item) => (
              <div key={item.productId} className="flex items-center gap-2 px-3 py-1">
                <span className="text-2xs text-ink min-w-0 flex-1 truncate">
                  {item.productName ?? `Product #${item.productId}`}
                </span>
                {item.isPreferred ? <Badge tone="signal">preferred</Badge> : null}
                <span className="text-2xs text-ink-muted shrink-0 font-mono tabular-nums">
                  {money(item.unitCost)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
